import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Plus, Trophy } from "@phosphor-icons/react";

const STAGES = ["submitted", "screened", "interviewing", "offered", "hired", "rejected", "hired_bonus_paid"];
const STAGE_STYLE = {
  submitted: "bg-zinc-50 border-zinc-200 text-zinc-700",
  screened: "bg-blue-50 border-blue-200 text-blue-800",
  interviewing: "bg-amber-50 border-amber-200 text-amber-800",
  offered: "bg-purple-50 border-purple-200 text-purple-800",
  hired: "bg-emerald-50 border-emerald-200 text-emerald-800",
  rejected: "bg-red-50 border-red-200 text-red-800",
  hired_bonus_paid: "bg-adb-green/10 border-adb-green/40 text-adb-navy",
};

export default function Referrals() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ candidate_name: "", candidate_email: "", candidate_phone: "", job_id: "", role_title: "", relationship: "ex-colleague", note: "" });

  const load = () => {
    api.get("/referrals", { params }).then(({ data }) => setRows(data)).catch(() => {});
    api.get("/referrals/stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
    api.get("/jobs", { params }).then(({ data }) => setJobs(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.job_id) delete payload.job_id;
      await api.post("/referrals", payload);
      toast.success("Referral submitted");
      setOpen(false);
      setForm({ candidate_name: "", candidate_email: "", candidate_phone: "", job_id: "", role_title: "", relationship: "ex-colleague", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const advance = async (id, stage) => { try { await api.patch(`/referrals/${id}?stage=${stage}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Talent · Referrals</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Employee referrals</h1>
          <p className="text-sm text-zinc-500 mt-2">Refer great people, earn bonuses when they join. HR tracks progress from submission to bonus payout.</p>
        </div>
        {user?.employee_id && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ref-add-btn"><UserPlus size={16} className="mr-1.5" /> Refer someone</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Refer a candidate</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Candidate name *</Label><Input value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} className="rounded-sm" data-testid="ref-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Email *</Label><Input type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} className="rounded-sm font-mono-plex" data-testid="ref-email-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={form.candidate_phone} onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Referring for a job?</Label>
                  <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="ref-job-select"><SelectValue placeholder="Select an open role (optional)" /></SelectTrigger>
                    <SelectContent>{jobs.filter((j) => j.status === "open").map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Or describe the role</Label><Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Relationship</Label>
                  <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{["ex-colleague", "friend", "family", "network", "other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Why they'd be great</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="rounded-sm" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} disabled={!form.candidate_name || !form.candidate_email} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="ref-submit-btn">Refer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isHR && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total referrals" value={stats.total} />
          <StatCard label="Interviewing" value={stats.stages.interviewing} />
          <StatCard label="Hired" value={stats.stages.hired + stats.stages.hired_bonus_paid} accent="text-emerald-700" />
          <StatCard label="Bonus paid" value={`₹ ${stats.total_bonus_paid.toLocaleString("en-IN")}`} accent="text-adb-navy" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-sm" data-testid="ref-list">
          <div className="px-4 py-2 border-b border-zinc-100 font-display font-bold text-sm">Referral pipeline</div>
          <div className="overflow-x-auto">
            <table className="hr-table">
              <thead><tr><th>Candidate</th><th>Referred by</th><th>Role</th><th>Stage</th>{isHR && <th></th>}</tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={isHR ? 5 : 4} className="text-center py-6 text-zinc-500 text-sm">No referrals yet.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="text-sm">{r.candidate_name}</div><div className="text-[10px] font-mono-plex text-zinc-500">{r.candidate_email}</div></td>
                    <td className="text-xs">{r.referrer_name}</td>
                    <td className="text-xs">{r.role_title || "—"}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${STAGE_STYLE[r.stage]}`}>{r.stage.replace(/_/g, " ")}</Badge></td>
                    {isHR && (
                      <td>
                        <Select value={r.stage} onValueChange={(v) => advance(r.id, v)}>
                          <SelectTrigger className="rounded-sm h-7 text-xs w-[130px]" data-testid={`ref-stage-${r.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="flex items-center gap-2"><Trophy size={16} className="text-adb-green" weight="fill" /><div className="font-display font-bold text-sm">Top referrers</div></div>
          <div className="mt-3 space-y-1.5">
            {(!stats || stats.top_referrers.length === 0) && <div className="text-xs text-zinc-400 text-center py-4">Be the first to refer!</div>}
            {stats?.top_referrers.map((r, i) => (
              <div key={r.employee_id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-zinc-50">
                <div className={`w-6 h-6 grid place-items-center rounded-sm font-display font-bold text-xs ${i === 0 ? "bg-adb-green text-adb-navy" : "bg-zinc-100 text-zinc-500"}`}>{i + 1}</div>
                <div className="text-xs flex-1 truncate">{r.name}</div>
                <div className="text-xs font-mono-plex font-semibold text-adb-navy">{r.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${accent || "text-adb-navy"}`}>{value}</div>
    </div>
  );
}
